/**
 * BootSequenceStyles — token-driven, lit-CRT panel material.
 * All colours resolve from palette.ts / COLORS / uiTokens — NO new gameplay hex.
 * Replaces the old bespoke chrome (Courier, flat borders, one-off animations).
 */
import { COLORS } from "../core/constants.ts";
import { ECHELON } from "../rendering/designTokens.ts";
import {
  UI_CHROME,
  UI_FONT_STACK,
  roleFontSize,
  withUiScale,
} from "./uiTokens.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";

export function getBootStyles(): string {
  const G = COLORS.SENTINEL_GREEN;
  const GH = UI_CHROME.greenHi ?? "#BFFFB0";
  const CRIM = COLORS.ORACLE_RED;
  const GOLD = UI_CHROME.etchAccent;
  const AMB = COLORS.AMBER_WARN;
  const PB = UI_CHROME.panelBorder;
  const GT = UI_CHROME.groundTop;
  const GB = UI_CHROME.groundBottom;
  const DIM = UI_CHROME.dim ?? "#7d8aa0";
  const STEEL = UI_CHROME.steel ?? "#cfe0ff";
  const ECH = ECHELON.SIGNAL;
  const GDK = UI_CHROME.greenDk ?? "#1C7A12";
  const rm = isReducedMotion();
  return `
#boot-sequence {
  /* S183/BL-201 — absolute (was fixed) so the boot screen uses the IDENTICAL
     frame envelope as every other screen: it mounts into #game-container (the
     1600x900 stage, position:absolute), so inset:0 fills exactly that frame.
     fixed rendered the same only by accident of the stage's transform creating a
     containing block; absolute is the semantically-correct, drift-proof form. */
  position:absolute; inset:0; z-index:200;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  background:radial-gradient(130% 90% at 50% 8%,#0b110c 0%,#05070b 64%);
  font-family:${UI_FONT_STACK}; color:${G};
  overflow:hidden; outline:none;
}

/* P1-3 idle throttle: after ~45 s with no input the title screen pauses every
   animation (the JS metric/glitch loops are cleared in BootSequence) so an idle
   device stops burning battery/GPU. Any tap/key/move resumes it. */
#boot-sequence.boot-idle, #boot-sequence.boot-idle * { animation-play-state: paused !important; }

/* Living atmosphere */
.boot-atmosphere { position:absolute; inset:0; z-index:0; pointer-events:none; overflow:hidden; }
.boot-atmo-grid {
  position:absolute; inset:-12%;
  background:linear-gradient(rgba(136, 224, 208,.06) 1px,transparent 1px) 0 0/46px 46px,
    linear-gradient(90deg,rgba(136, 224, 208,.06) 1px,transparent 1px) 0 0/46px 46px;
  opacity:.30; ${rm ? "" : "animation:boot-griddrift 22s linear infinite;"}
}
@keyframes boot-griddrift{0%{background-position:0 0,0 0}100%{background-position:0 46px,46px 0}}
.boot-atmo-floor {
  position:absolute; left:-30%; right:-30%; bottom:-14%; height:52%;
  background:linear-gradient(rgba(136, 224, 208,.08) 1px,transparent 1px) 0 0/58px 58px,
    linear-gradient(90deg,rgba(136, 224, 208,.08) 1px,transparent 1px) 0 0/58px 58px;
  transform:perspective(560px) rotateX(66deg); transform-origin:bottom center; opacity:.30;
  -webkit-mask-image:linear-gradient(transparent,#000 70%); mask-image:linear-gradient(transparent,#000 70%);
  ${rm ? "" : "animation:boot-griddrift 18s linear infinite;"}
}
.boot-atmo-haze {
  position:absolute; inset:0;
  background:radial-gradient(70% 42% at 50% 52%,rgba(136, 224, 208,.07),transparent 60%),
    radial-gradient(46% 34% at 88% 14%,rgba(232, 154, 184,.07),transparent 60%);
}
.boot-atmo-vig { position:absolute; inset:0; background:radial-gradient(130% 108% at 50% 46%,transparent 48%,rgba(0,0,0,.66) 100%); }

/* Grid layout */
.boot-grid {
  position:relative; z-index:1; flex:1; min-height:0;
  display:grid; grid-template-columns:300px minmax(0,1fr) 300px; gap:14px;
  padding:16px 16px 6px; width:min(1480px,100%); max-height:100%;
  transition:opacity 0.3s;
}
.boot-col { display:flex; flex-direction:column; justify-content:space-between; gap:12px; min-width:0; min-height:0; }
.boot-col.center { align-items:center; justify-content:center; gap:0; text-align:center; }

/* Shared lit-CRT panel material */
.boot-panel {
  position:relative; border:1px solid ${PB}; border-radius:7px; overflow:hidden;
  background:linear-gradient(180deg,${GT},${GB});
  box-shadow:0 0 14px -4px rgba(136, 224, 208,.12) inset,0 0 0 1px rgba(136, 224, 208,.04);
  opacity:0; transform:translateY(8px) scale(0.96); transition:none;
}
.boot-panel.panel-in { opacity:1; transform:translateY(0) scale(1); ${
    rm
      ? ""
      : "animation:boot-panel-slam 0.2s cubic-bezier(0.2,1.4,0.4,1) forwards;"
  } }
@keyframes boot-panel-slam { from{opacity:0;transform:translateY(12px) scale(0.92)} 60%{transform:translateY(-2px) scale(1.01)} to{opacity:1;transform:translateY(0) scale(1)} }
.boot-panel.crimson { border-color:rgba(232, 154, 184,.24); box-shadow:0 0 16px -4px rgba(232, 154, 184,.16) inset,0 0 0 1px rgba(232, 154, 184,.06); }

/* Panel header */
.boot-ph { display:flex; align-items:center; gap:7px; padding:6px 10px; font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.16em; color:#7fae84;
  border-bottom:1px solid ${PB}; background:linear-gradient(180deg,rgba(136, 224, 208,.05),transparent); }
.boot-ph .dot { width:6px; height:6px; border-radius:50%; background:${G}; box-shadow:0 0 6px ${G}; }
.boot-ph.r { color:rgba(255,199,212,1); }
.boot-ph.r .dot { background:${CRIM}; box-shadow:0 0 6px ${CRIM}; ${
    rm ? "" : "animation:boot-rec 1.1s steps(2,end) infinite;"
  } }
@keyframes boot-rec{0%,55%{opacity:1}56%,100%{opacity:.2}}
.boot-pb { padding:9px 11px; position:relative; }

/* Board chrome: scan + etch */
.boot-scan { position:absolute; inset:0; z-index:6; pointer-events:none; mix-blend-mode:overlay; opacity:.32;
  background:repeating-linear-gradient(0deg,rgba(255,255,255,.05) 0 1px,transparent 1px 3px); }
.boot-etch { position:absolute; width:16px; height:16px; z-index:7; opacity:.7; pointer-events:none; }
.boot-etch::before,.boot-etch::after { content:""; position:absolute; background:var(--etch-ac,${GOLD}); }
.boot-etch.t { top:5px; } .boot-etch.b { bottom:5px; } .boot-etch.l { left:5px; } .boot-etch.r { right:5px; }
.boot-etch.t::before { top:0; width:10px; height:1.5px; } .boot-etch.t::after { top:0; width:1.5px; height:7px; }
.boot-etch.b::before { bottom:0; width:10px; height:1.5px; } .boot-etch.b::after { bottom:0; width:1.5px; height:7px; }
.boot-etch.l::before,.boot-etch.l::after { left:0; } .boot-etch.r::before,.boot-etch.r::after { right:0; }

/* KV rows */
.boot-kv { display:flex; justify-content:space-between; gap:8px; padding:2px 0; font-size:${roleFontSize(
    "micro"
  )}; }
.boot-kv .k { color:${DIM}; letter-spacing:.06em; }
.boot-kv .v { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "label"
  )}; color:${STEEL}; letter-spacing:.03em; }
.boot-kv .v.g { color:${GH}; } .boot-kv .v.r { color:${CRIM}; } .boot-kv .v.a { color:${GOLD}; text-shadow:0 0 6px rgba(255,194,61,.5); }

.boot-sub { margin-top:8px; padding-top:7px; border-top:1px dotted rgba(136, 224, 208,.16); font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.16em; color:#6f8a72; }
.boot-sub.r { border-color:rgba(232, 154, 184,.2); color:rgba(255,199,212,1); }
.boot-sub.first { margin-top:0; padding-top:0; border-top:none; }

/* Data-honest bars */
.boot-brow { display:grid; grid-template-columns:92px 1fr 50px; align-items:center; gap:8px; padding:3px 0; font-size:${roleFontSize(
    "micro"
  )}; }
.boot-brow .k { color:${DIM}; letter-spacing:.04em; }
.boot-brow .v { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "label"
  )}; text-align:right; color:${STEEL}; }
.boot-brow .v.g { color:${GH}; } .boot-brow .v.r { color:${CRIM}; } .boot-brow .v.a { color:${GOLD}; text-shadow:0 0 6px rgba(255,194,61,.5); }
.boot-dbar { height:6px; border-radius:3px; background:rgba(136, 224, 208,.07); border:1px solid rgba(136, 224, 208,.16); overflow:hidden; }
.boot-dbar i { display:block; height:100%; border-radius:3px; background:linear-gradient(90deg,${GDK},${G}); box-shadow:0 0 7px rgba(136, 224, 208,.45); transition:width .5s ease; }
.boot-dbar.r { background:rgba(232, 154, 184,.07); border-color:rgba(232, 154, 184,.18); }
.boot-dbar.r i { background:linear-gradient(90deg,#7a0b2a,${CRIM}); box-shadow:0 0 7px rgba(232, 154, 184,.45); }

/* OK chips */
.boot-stat { display:flex; justify-content:space-between; align-items:center; padding:3px 0; font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.05em; color:#9fb0a4; }
.boot-ok { display:inline-flex; align-items:center; gap:5px; font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "caption"
  )}; color:${G}; }
.boot-ok b { width:7px; height:7px; background:${G}; box-shadow:0 0 6px ${G}; border-radius:1px; display:inline-block; }

/* High scores */
.boot-score-tabs { display:flex; gap:6px; margin-bottom:6px; }
.boot-score-tab { font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.14em; padding:3px 9px; border:1px solid ${PB}; border-radius:4px; color:${DIM}; cursor:pointer; background:transparent; font-family:${UI_FONT_STACK}; }
.boot-score-tab-active { color:#06150d; background:${G}; border-color:${G}; box-shadow:0 0 12px -2px rgba(136, 224, 208,.6); }
.boot-score-panel { display:none; }
.boot-score-panel-active { display:block; }
/* Owner decision 2026-08-18 (top-10 + own rank): the GLOBAL pane is a FIXED
   scroll viewport of ≈ the local pane's five rows, so the ten-row list plus
   the player's own row can never grow the frozen boot layout — extra rows
   scroll inside the pane. The cap rides the same --ui-scale/--chrome-scale
   chain as the row type (withUiScale), so the phone tier keeps ~5 rows too. */
#boot-scores-global.boot-score-panel-active { max-height:${withUiScale(
    138
  )}; overflow-y:auto; overscroll-behavior:contain; scrollbar-width:thin; }
/* Rank-gap separator: rendered wherever the rank sequence jumps (top-10 … own
   row at rank 102342), purely data-driven — see BootSequence.renderGlobalRows. */
.boot-score-gap { text-align:center; color:${DIM}; font-size:${roleFontSize(
    "micro"
  )}; line-height:1; padding:1px 0 2px; }
/* GLOBAL rows re-balance the frozen local grid for Game Center data: the rank
   cell may widen (a six-digit own rank cannot fit 28px), the NAME takes
   the flexible width — GC aliases are real names, not 3-char initials — and
   ellipsizes instead of colliding with its neighbours. The date column is
   auto: GC rows omit the cell entirely (no date exists), D1 web rows keep it. */
#boot-scores-global .boot-score-row { grid-template-columns:minmax(28px,auto) 1fr auto auto; }
#boot-scores-global .boot-score-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
/* The player's own GLOBAL row pins to the bottom of the scroll viewport — the
   own rank is readable without scrolling. Solid ground (panel bottom color) so
   rows scrolling beneath cannot bleed through the sticky layer. */
#boot-scores-global .boot-score-row-self { position:sticky; bottom:0; background:${GB}; }
.boot-score-row { display:grid; grid-template-columns:28px 42px 1fr auto; gap:0 6px; align-items:center; padding:4px 6px; border-radius:5px; font-size:${roleFontSize(
    "micro"
  )}; }
.boot-score-row:nth-child(odd) { background:rgba(136, 224, 208,.04); }
.boot-score-row-self { box-shadow:0 0 0 1px rgba(136, 224, 208,.4) inset; }
.boot-score-rank { color:${G}; font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "label"
  )}; }
.boot-score-name { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; letter-spacing:.18em; color:${GH}; }
.boot-score-pts { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "body"
  )}; color:${GOLD}; letter-spacing:.02em; text-shadow:0 0 6px rgba(255,194,61,.4); }
.boot-score-date { font-size:${roleFontSize(
    "micro"
  )}; color:${DIM}; flex:1; text-align:left; }
.boot-score-offline { font-size:${roleFontSize(
    "micro"
  )}; line-height:1.8; color:${AMB}; letter-spacing:1px; text-shadow:0 0 5px rgba(232, 208, 152,0.35); padding:4px 0; }
/* WOW Pass 2 (item 1) — the GLOBAL "SIGN IN TO COMPETE" graceful degrade (iOS
   Game Center, signed-out): an amber compete CTA + the local best + an optional
   VIEW GLOBAL RANKS action. Static (reduced-motion-safe); central tokens only. */
.boot-score-signin { font-size:${roleFontSize(
    "micro"
  )}; line-height:1.6; color:${AMB}; letter-spacing:1px; text-shadow:0 0 6px rgba(232, 208, 152,0.4); padding:4px 0 2px; }
.boot-score-localbest { font-size:${roleFontSize(
    "micro"
  )}; color:${G}; letter-spacing:.06em; text-shadow:0 0 6px rgba(136, 224, 208,.4); padding-bottom:6px; }
.boot-score-ranks-btn { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.12em; padding:5px 10px; color:${G}; background:transparent; border:1px solid ${G}; border-radius:4px; cursor:pointer; box-shadow:0 0 12px -3px rgba(136, 224, 208,.5); }
.boot-score-ranks-btn:hover { border-color:${GH}; box-shadow:0 0 16px -2px rgba(136, 224, 208,.7); }

/* GLOBAL DEFENSE LOG (E5) — the population switcher row. Sits between the
   LOCAL/GLOBAL tabs and the rows: same monospace register as the tabs, arrows
   sized to the 44px touch floor on coarse pointers so it is usable on the
   phone. */
.boot-score-level {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 2px 0 4px;
}
.boot-score-level-arrow {
  background: transparent;
  border: 0;
  color: ${G};
  font: inherit;
  font-size: 15px;
  line-height: 1;
  padding: 4px 8px;
  cursor: pointer;
  opacity: 0.75;
}
.boot-score-level-arrow:hover,
.boot-score-level-arrow:focus-visible {
  opacity: 1;
}
@media (pointer: coarse) {
  .boot-score-level-arrow {
    min-width: 44px;
    min-height: 44px;
  }
}
.boot-score-level-label {
  min-width: 11ch;
  text-align: center;
  letter-spacing: 0.14em;
  opacity: 0.9;
}

/* GLOBAL DEFENSE LOG highlight — a gentle pulsing glow + a gold trophy star draw the
   eye to the records (this panel only). The wrap pulses so the panel keeps its
   slam entry; the star sits outside the panel's overflow:hidden so it overhangs
   the top-right corner. Sits on the dark panel (transparent SVG → no white box). */
.boot-records-wrap { position:relative; border-radius:7px; ${
    rm ? "" : "animation:boot-records-pulse 2.8s ease-in-out infinite;"
  } }
@keyframes boot-records-pulse { 0%,100% { box-shadow:0 0 14px -6px rgba(136, 224, 208,.28); } 50% { box-shadow:0 0 28px -3px rgba(136, 224, 208,.6); } }
.boot-panel--records { border-color:rgba(136, 224, 208,.34); }
.boot-records-star { position:absolute; top:-20px; right:-15px; width:68px; height:68px; z-index:9; pointer-events:none;
  filter:drop-shadow(0 0 7px rgba(255,200,80,.55)) drop-shadow(0 2px 3px rgba(0,0,0,.6));
  ${
    rm
      ? "transform:rotate(-5deg);"
      : "animation:boot-records-star 3.6s ease-in-out infinite;"
  } }
.boot-records-star svg { display:block; width:100%; height:100%; }
@keyframes boot-records-star { 0%,100% { transform:rotate(-5deg) scale(1); } 50% { transform:rotate(-1deg) scale(1.06); } }

/* Command queue */
.boot-cmd { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "label"
  )}; letter-spacing:.03em; color:${G}; padding:2px 0; }
.boot-cmd.dim { color:${GDK}; }
.boot-cmd .car { ${rm ? "" : "animation:boot-blink 1s steps(2) infinite;"} }
@keyframes boot-blink{50%{opacity:0}}

/* CENTER: AI-CORE hero */
.boot-aicore { position:relative; display:flex; align-items:center; justify-content:center; gap:48px; height:194px; margin-top:2px; width:100%; }
.boot-aifig { z-index:2; } .boot-aifig svg { display:block; width:116px; height:auto; }
.boot-aifig.p { filter:drop-shadow(0 0 15px rgba(136, 224, 208,.55)); }
.boot-aifig.o { filter:drop-shadow(0 0 15px rgba(232, 154, 184,.5)); }
.boot-hex { position:relative; width:96px; height:96px; display:flex; align-items:center; justify-content:center; z-index:2; }
.boot-hex svg { width:96px; height:96px; filter:drop-shadow(0 0 12px rgba(136, 224, 208,.6)); ${
    rm ? "" : "animation:boot-hexspin 14s linear infinite;"
  } }
@keyframes boot-hexspin{to{transform:rotate(360deg)}}
.boot-hex .shield { position:absolute; width:124px; height:124px; border:1.5px solid rgba(136, 224, 208,.45); border-radius:50%;
  box-shadow:0 0 16px rgba(136, 224, 208,.35),inset 0 0 16px rgba(136, 224, 208,.25); ${
    rm ? "" : "animation:boot-shield 2.6s ease-in-out infinite;"
  } }
@keyframes boot-shield{0%,100%{transform:scale(.92);opacity:.45}50%{transform:scale(1.07);opacity:.9}}
.boot-hex .core { position:absolute; width:18px; height:18px; border-radius:50%; background:radial-gradient(circle,#eafff0,${G}); box-shadow:0 0 18px ${G};
  ${rm ? "" : "animation:boot-corepulse 2.4s ease-in-out infinite;"} }
@keyframes boot-corepulse{0%,100%{transform:scale(.85);opacity:.85}50%{transform:scale(1.15);opacity:1}}
.boot-hexlabel { position:absolute; bottom:-14px; left:50%; transform:translateX(-50%); font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.24em; color:#6f8a72; }
.boot-beams { position:absolute; inset:0 0 24px; z-index:1; pointer-events:none; }
.boot-beam { position:absolute; top:50%; height:3px; transform:translateY(-50%); border-radius:3px; }
.boot-beam.g { left:24%; width:16%; background:linear-gradient(90deg,transparent,rgba(136, 224, 208,.65)); box-shadow:0 0 9px rgba(136, 224, 208,.5); }
.boot-beam.r { right:24%; width:16%; background:linear-gradient(270deg,transparent,rgba(232, 154, 184,.5)); box-shadow:0 0 9px rgba(232, 154, 184,.4); opacity:.8; }
.boot-beam i { position:absolute; top:50%; width:8px; height:8px; margin-top:-4px; border-radius:50%; }
.boot-beam.g i { background:radial-gradient(circle,#eafff0,${G}); box-shadow:0 0 9px ${G}; ${
    rm ? "" : "animation:boot-flowR 1.7s linear infinite;"
  } }
.boot-beam.r i { background:radial-gradient(circle,#ffd0dc,${CRIM}); box-shadow:0 0 9px ${CRIM}; ${
    rm ? "" : "animation:boot-flowL 2.3s linear infinite;"
  } }
@keyframes boot-flowR{0%{left:-7px;opacity:0}14%{opacity:1}86%{opacity:1}100%{left:100%;opacity:0}}
@keyframes boot-flowL{0%{right:-7px;opacity:0}14%{opacity:1}100%{right:100%;opacity:0}}
.boot-orb { position:absolute; border-radius:50%; ${
    rm ? "" : "animation:boot-float 5s ease-in-out infinite;"
  } }
@keyframes boot-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
.boot-activeline { position:absolute; bottom:2px; left:50%; transform:translateX(-50%); z-index:2;
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "caption"
  )}; letter-spacing:.14em; color:#6f8a72; white-space:nowrap; }
.boot-activeline b { color:${G}; } .boot-activeline .r { color:${CRIM}; }

/* A - Logo hero (owner: a transparent pixel-art PNG; the green neon glow lives on
   the WRAPPER via drop-shadow so it follows the logo's alpha shape — no box — and
   doesn't fight the img's own reveal-filter animation). */
.boot-logowrap { position:relative; padding:14px 0 6px; display:flex; justify-content:center;
  filter:drop-shadow(0 0 6px rgba(136, 224, 208,.65)) drop-shadow(0 0 20px rgba(136, 224, 208,.4)) drop-shadow(0 0 48px rgba(136, 224, 208,.2)); }
/* S142/BL-130 — cap trimmed (640→560) so the Glacier logo sits balanced in the
   hero block (was slightly oversized against the new brand mark). */
.boot-logo { display:block; width:min(560px,92%); height:auto;
  ${
    rm
      ? ""
      : "animation:boot-logoboot 1.5s ease-out 1 both,boot-logoflicker 7s steps(1,end) 1.7s infinite;"
  } }
@keyframes boot-logoboot{0%{opacity:0;filter:brightness(3.4) blur(5px);clip-path:inset(0 100% 0 0)}14%{opacity:1;filter:brightness(2.2) blur(1px)}40%{clip-path:inset(0 0 0 0)}46%{opacity:.55}52%{opacity:1;filter:brightness(1.2)}100%{opacity:1;filter:none;clip-path:inset(0 0 0 0)}}
@keyframes boot-logoflicker{0%,46%,50%,92%,95%,100%{opacity:1}48%{opacity:.82}49%{opacity:1}93%{opacity:.88}94%{opacity:1}}
.boot-title-block { position:relative; }
/* BL-226 — the publisher brand ("peerapps") under the AI WARS logo: a small,
   premium gold-etch credit, letter-spaced, flanked by faint marks. */
.boot-brand {
  text-align:center; margin-top:3px; font-family:${UI_FONT_STACK};
  font-size:${roleFontSize("caption")}; letter-spacing:.46em; color:${GOLD};
  text-shadow:0 0 10px color-mix(in srgb, ${GOLD} 45%, transparent); opacity:.9;
}
.boot-brand .br { color:${GOLD}; opacity:.5; letter-spacing:0; }

/* Tagline */
.boot-tag { margin-top:4px; display:flex; flex-direction:column; align-items:center; gap:3px; }
.boot-tag .lead { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "title"
  )}; letter-spacing:.22em; color:${GH}; text-shadow:0 0 12px rgba(136, 224, 208,.5); }
.boot-tag .lead .br { color:${GDK}; }
.boot-tag .story { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "heading"
  )}; line-height:1.5; color:#9fb0a4; }
.boot-tag .story .r { color:${CRIM}; text-shadow:0 0 8px rgba(232, 154, 184,.4); }
.boot-tag .story .g { color:${GH}; }
.boot-tag .ver { font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.26em; color:#5e6f74; margin-top:3px; }

/* CTA area */
.boot-cta-area { display:flex; flex-direction:column; align-items:center; gap:0; opacity:0; }
.boot-cta-area.panel-in { opacity:1; }
.boot-cta-block { margin-top:18px; display:flex; flex-direction:column; align-items:center; gap:11px; }
.boot-term { font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "title"
  )}; letter-spacing:.04em; color:${GH}; text-shadow:0 0 12px rgba(136, 224, 208,.55); }
.boot-term .p { color:${G}; } .boot-term .car { ${
    rm ? "" : "animation:boot-blink 1s steps(2) infinite;"
  } }
/* (Owner: the oscillating "SYSTEM CHARGE" bar was removed entirely — too much.) */
#boot-press { position:relative; overflow:hidden; border:1px solid ${G}; border-radius:8px; padding:13px 48px; cursor:pointer;
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "title"
  )}; letter-spacing:.28em; color:${GH};
  background:radial-gradient(120% 160% at 50% 40%,rgba(136, 224, 208,.14),transparent);
  ${rm ? "" : "animation:boot-bootpulse 2.4s ease-in-out infinite;"} }
@keyframes boot-bootpulse{0%,100%{box-shadow:0 0 22px -8px rgba(136, 224, 208,.6),0 0 0 1px rgba(136, 224, 208,.3) inset}50%{box-shadow:0 0 40px -4px rgba(136, 224, 208,.9),0 0 0 1px rgba(136, 224, 208,.55) inset}}
#boot-press .br { color:${G}; }
/* F15-4 — one prompt, two wordings, picked by the device's PRIMARY pointer. The
   button has always been tappable (the boot overlay's click handler routes any
   click inside #boot-press to transition(), and the e2e harness proves it with a
   real touchscreen tap), so this is a copy defect, not a missing input path: on
   an iPhone the screen asked for a key the device does not have. Display-only —
   no box, no length, no scale maths is touched, and both strings ship so the
   swap needs no re-render. */
#boot-press .boot-press-tap { display:none; }
#boot-press .foil { position:absolute; inset:0; background:linear-gradient(110deg,transparent 42%,rgba(255,255,255,.4) 50%,transparent 58%);
  ${rm ? "display:none;" : "animation:boot-foil 2.8s linear infinite;"} }
@keyframes boot-foil{0%{transform:translateX(-120%)}100%{transform:translateX(120%)}}
#boot-press:hover { animation:none; color:${CRIM}; border-color:${CRIM};
  text-shadow:0 0 16px ${CRIM},0 0 32px rgba(255,45,85,.4);
  box-shadow:0 0 20px rgba(255,45,85,.3),inset 0 0 12px rgba(255,45,85,.1); }

/* Footer */
.boot-footer { position:relative; z-index:1; display:flex; align-items:center; justify-content:space-between;
  padding:6px 18px 4px; font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.14em; color:#5e6f74; border-top:1px solid ${PB}; opacity:0; }
.boot-footer.panel-in { opacity:1; }
.boot-footer .clr { color:${GOLD}; display:inline-flex; align-items:center; gap:6px; text-shadow:0 0 6px rgba(255,194,61,.4); }
.boot-footer .clr b { width:6px; height:6px; background:${GOLD}; box-shadow:0 0 6px ${GOLD}; display:inline-block; border-radius:1px; }

/* C - CRT tube overlay */
.boot-crt { position:absolute; inset:0; z-index:8; pointer-events:none; border-radius:10px; overflow:hidden; opacity:.6;
  background:repeating-linear-gradient(0deg,rgba(0,0,0,.10) 0 1px,transparent 1px 2.5px);
  box-shadow:inset 0 0 110px 8px rgba(0,0,0,.4); ${
    rm ? "" : "animation:boot-crtflicker 6s steps(1,end) infinite;"
  } }
.boot-crt::after { content:""; position:absolute; inset:0; background:linear-gradient(rgba(120,255,150,.05),transparent 14%); background-size:100% 220%;
  ${rm ? "" : "animation:boot-crtroll 9s linear infinite;"} }
@keyframes boot-crtflicker{0%,95%,100%{opacity:.6}96%{opacity:.46}97%{opacity:.62}98%{opacity:.52}}
@keyframes boot-crtroll{0%{background-position:0 -110%}100%{background-position:0 110%}}

/* POST overlay */
.boot-post { position:absolute; inset:0; z-index:100; background:#020502; padding:40px;
  display:flex; flex-direction:column; justify-content:center; transition:opacity 0.4s; overflow:hidden; }
.boot-post.post-fadeout { opacity:0; pointer-events:none; }
.post-bg-pulse { position:absolute; inset:-50%; background:radial-gradient(ellipse at center,rgba(136, 224, 208,.04),transparent 60%);
  ${
    rm ? "" : "animation:boot-bgbreathe 4s ease-in-out infinite;"
  } pointer-events:none; z-index:0; }
@keyframes boot-bgbreathe{0%,100%{transform:scale(1);opacity:.6}50%{transform:scale(1.3);opacity:1}}
.post-scanlines { position:absolute; inset:0; pointer-events:none; z-index:1;
  background:repeating-linear-gradient(0deg,transparent 0 3px,rgba(136, 224, 208,.03) 3px 4px); }
.post-scanlines::after { content:""; position:absolute; left:0; right:0; height:4px; background:rgba(136, 224, 208,.08);
  ${rm ? "" : "animation:boot-scanmove 8s linear infinite;"} }
@keyframes boot-scanmove{from{top:-4px}to{top:100%}}
.post-noise { position:absolute; inset:0; opacity:.03; pointer-events:none; z-index:1;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  ${rm ? "" : "animation:boot-noiseshift 0.5s steps(5) infinite;"} }
@keyframes boot-noiseshift{to{transform:translate(2px,-2px)}}
.post-vignette { position:absolute; inset:0; pointer-events:none; z-index:1; background:radial-gradient(ellipse at center,transparent 55%,rgba(0,0,0,.6) 100%); }
.post-content { position:relative; z-index:2; }
.post-line { font-size:${roleFontSize(
    "label"
  )}; line-height:2.2; color:${G}; text-shadow:0 0 4px rgba(136, 224, 208,.5);
  ${rm ? "" : "animation:boot-linein 0.15s ease-out;"} }
.post-line-threat { color:${CRIM} !important; text-shadow:0 0 8px ${CRIM},0 0 16px rgba(255,45,85,.3) !important;
  ${
    rm
      ? ""
      : "animation:boot-linein 0.15s ease-out,boot-threatglitch 0.4s ease-out !important;"
  } }
/* S126: the ECHELON intrusion line carries HER corruption magenta — her
   signature is distinct from generic alarm-crimson from the first second. */
.post-line-echelon { color:${ECH} !important; text-shadow:0 0 8px ${ECH},0 0 16px rgba(209,32,140,.35) !important; }
@keyframes boot-linein{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
@keyframes boot-threatglitch{0%{transform:translateX(0)}20%{transform:translateX(6px) skewX(-2deg)}40%{transform:translateX(-4px) skewX(1deg)}80%{transform:translateX(-1px)}100%{transform:translateX(0)}}
.boot-threat-flash { position:absolute; inset:0; z-index:99; pointer-events:none;
  background:radial-gradient(ellipse at center,rgba(255,45,85,.15),transparent 70%);
  ${
    rm ? "display:none;" : "animation:boot-threatflash 0.6s ease-out forwards;"
  } }
@keyframes boot-threatflash{from{opacity:1}to{opacity:0}}

/* Signal loss */
.signal-loss-heavy { position:absolute; inset:0; z-index:200; pointer-events:none; ${
    rm ? "display:none;" : "animation:boot-sigheavy 0.6s steps(1) forwards;"
  } }
@keyframes boot-sigheavy{0%{background:#000;opacity:1}10%{background:#000;opacity:1}12%{background:rgba(255,45,85,.06);opacity:.4}14%{background:#000;opacity:1}25%{background:#000;opacity:.95}28%{background:transparent;opacity:0}32%{background:#0a0a0a;opacity:.85}36%{background:transparent;opacity:0}42%{background:#000;opacity:.6}45%{background:transparent;opacity:0}100%{background:transparent;opacity:0}}
.signal-loss-heavy::before { content:""; position:absolute; left:0; right:0; height:4px; top:35%; background:rgba(255,255,255,.7); ${
    rm ? "display:none;" : "animation:boot-tear-a 0.4s steps(3) forwards;"
  } }
@keyframes boot-tear-a{0%{top:25%;opacity:1}40%{top:55%;opacity:.6}100%{top:85%;opacity:0}}
.signal-loss-heavy::after { content:""; position:absolute; inset:0;
  background:repeating-linear-gradient(0deg,transparent 0,transparent 2px,rgba(255,255,255,.03) 2px,rgba(255,255,255,.03) 4px);
  ${rm ? "display:none;" : "animation:boot-staticnoise 0.12s steps(4) 3;"} }
.signal-loss-med { position:absolute; inset:0; z-index:200; pointer-events:none; ${
    rm ? "display:none;" : "animation:boot-sigmed 0.35s steps(1) forwards;"
  } }
@keyframes boot-sigmed{0%{background:#000;opacity:.9}15%{background:transparent;opacity:0}20%{background:#111;opacity:.8}30%{background:transparent;opacity:0}40%{background:#000;opacity:.5}48%{background:transparent;opacity:0}100%{background:transparent;opacity:0}}
.signal-loss-med::before { content:""; position:absolute; left:0; right:0; height:2px; top:60%; background:rgba(255,255,255,.4); ${
    rm ? "display:none;" : "animation:boot-tear-b 0.25s steps(2) forwards;"
  } }
@keyframes boot-tear-b{0%{top:50%;opacity:1}100%{top:70%;opacity:0}}
.signal-loss-light { position:absolute; inset:0; z-index:200; pointer-events:none; ${
    rm ? "display:none;" : "animation:boot-siglight 0.2s steps(1) forwards;"
  } }
@keyframes boot-siglight{0%{background:#000;opacity:.7}25%{background:transparent;opacity:0}35%{background:#0a0a0a;opacity:.4}50%{background:transparent;opacity:0}100%{background:transparent;opacity:0}}
.signal-pre-flicker { position:absolute; inset:0; z-index:200; pointer-events:none; ${
    rm ? "display:none;" : "animation:boot-preflick 0.15s steps(1) forwards;"
  } }
@keyframes boot-preflick{0%{background:#000;opacity:.3}40%{background:transparent;opacity:0}60%{background:#000;opacity:.15}100%{background:transparent;opacity:0}}
@keyframes boot-staticnoise{to{transform:translateY(4px)}}
.post-tear-heavy { ${
    rm ? "" : "animation:boot-tearshake-h 0.4s steps(2) forwards;"
  } }
@keyframes boot-tearshake-h{0%{transform:translateX(0) skewX(0);filter:hue-rotate(0deg)}12%{transform:translateX(16px) skewX(-4deg);filter:hue-rotate(50deg) brightness(0.2)}28%{transform:translateX(-10px) skewX(3deg);filter:hue-rotate(-30deg) brightness(1.6)}45%{transform:translateX(6px) skewX(-1.5deg);filter:hue-rotate(15deg)}65%{transform:translateX(-3px);filter:none}100%{transform:translateX(0);filter:none}}
.post-tear-med { ${
    rm ? "" : "animation:boot-tearshake-m 0.3s steps(2) forwards;"
  } }
@keyframes boot-tearshake-m{0%{transform:translateX(0);filter:none}20%{transform:translateX(8px) skewX(-2deg);filter:brightness(.4)}40%{transform:translateX(-5px) skewX(1deg);filter:brightness(1.3)}70%{transform:translateX(2px);filter:none}100%{transform:translateX(0);filter:none}}
.post-tear-light { ${
    rm ? "" : "animation:boot-tearshake-l 0.2s steps(1) forwards;"
  } }
@keyframes boot-tearshake-l{0%{transform:translateX(0)}30%{transform:translateX(4px) skewX(-1deg)}60%{transform:translateX(-2px)}100%{transform:translateX(0)}}
.glitch-flash { ${rm ? "" : "animation:boot-glitchflash 0.15s;"} }
@keyframes boot-glitchflash{0%{color:${G}}33%{color:${
    COLORS.ICE_CYAN
  };transform:translateX(2px)}66%{color:${CRIM};transform:translateX(-1px)}100%{color:${G};transform:translateX(0)}}
.boot-flash { ${
    rm ? "opacity:0;" : "animation:boot-flashout 0.2s ease-out forwards;"
  } }
@keyframes boot-flashout{0%{filter:brightness(1)}40%{filter:brightness(4)}100%{opacity:0;filter:brightness(0)}}
.boot-title-glitch { ${
    rm ? "" : "animation:boot-titleglitchfx 0.2s steps(2);"
  } }
@keyframes boot-titleglitchfx{0%{transform:scale(1) translate(0,0);filter:hue-rotate(0deg)}25%{transform:scale(1.01) translate(3px,-1px);filter:hue-rotate(90deg) brightness(1.3)}50%{transform:scale(.99) translate(-2px,2px);filter:hue-rotate(-60deg)}75%{transform:scale(1.01) translate(1px,-1px);filter:hue-rotate(180deg) brightness(1.5)}100%{transform:scale(1) translate(0,0);filter:hue-rotate(0deg)}}

/* No viewport-size media queries here: the boot screen renders inside the
   fixed 1600×900 stage and is scaled to fit as one unit, so a viewport-width
   breakpoint would misfire (the element is always 1600px wide regardless of the
   device). The whole composition simply scales down on small screens. */

/* GPU budget guard for touch devices (phones/tablets). Inside the fixed stage
   the boot screen is rasterised at the full 1600×900 design size × device DPR
   (≈4× a phone-sized viewport at DPR 3). Its heaviest full-screen effects — the
   CRT scanline + large inset shadow, the 3D-perspective floor grid, the SVG
   turbulence noise, and the wide-radius logo/star glows — overflow iOS Safari's
   compositor memory at that size and crash/reload the tab. Trim them on coarse
   pointers (a device query, not a viewport-size one, so it's stage-safe); the
   layout and read stay intact, just lighter. Desktop is untouched. */
@media (pointer:coarse) {
  .boot-crt { display:none; }
  .boot-atmo-floor { display:none; }
  .post-noise { display:none; }
  .boot-atmo-grid { animation:none; }
  .boot-logowrap { filter:drop-shadow(0 0 8px rgba(136, 224, 208,.55)); }
  .boot-records-star { filter:none; }
  /* BL-193 — 44px touch floor on the high-score tab switcher (cf. TowerSelector.ts:795-798). */
  .boot-score-tab { min-height: 44px; }
  /* WOW Pass 2 (item 1) — same touch floor on the VIEW GLOBAL RANKS button. */
  .boot-score-ranks-btn { min-height: 44px; }
  /* F15-4 — a coarse primary pointer has no space bar: say TAP, not PRESS SPACE.
     pointer:coarse (not any-pointer) deliberately, so an iPhone paired with a
     Bluetooth mouse keeps the keyboard wording — the same signal choice
     docs/IPHONE_IPAD_UX_STRATEGY.md §1 records for the tier test. (No backticks
     in this comment: it sits inside a template literal and one would close it.) */
  #boot-press .boot-press-key { display:none; }
  #boot-press .boot-press-tap { display:inline; }
}
/* NOTE: the OS reduced-motion media query is intentionally NOT honored here. The
   CRT/glitch boot atmosphere is identity-defining and stays ON regardless of the
   OS setting (MotionSettings neutralises that signal app-wide). */

/* ── BL-130 — the shared top-right ⚙ gear (chrome from GearMenu's boot sheet) ── */
.boot-gear-host { position:absolute; top:14px; right:18px; z-index:210; }
.boot-gear-line { display:flex; align-items:center; padding:4px 4px; }
.boot-gear-line > * { width:100%; }
/* The folded pill controls (language · audio · legal) — the top-bar control
   vocabulary, restated for the dropdown. */
.boot-gear-btn-pill {
  background:transparent; border:1px solid ${
    COLORS.SENTINEL_GREEN_DIM
  }; color:${G};
  font-family:${UI_FONT_STACK}; font-size:${roleFontSize(
    "micro"
  )}; letter-spacing:.12em;
  padding:4px 9px; border-radius:5px; cursor:pointer; transition:all .2s; text-align:left;
}
.boot-gear-btn-pill:hover { border-color:${G}; box-shadow:0 0 8px -2px ${G}; }
.boot-gear-btn-pill.muted { color:${
    COLORS.SENTINEL_GREEN_DIM
  }; text-decoration:line-through; opacity:.8; }
`;
}
